import { useState, useEffect } from 'react'
import {
  PhoneOff,
  Loader2,
  WifiOff,
  AlertTriangle,
  Mic,
  MicOff,
  PhoneForwarded,
  Headphones,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTelemetryStore } from '../../stores/telemetryStore'
import { useEndCall } from '../../api/calls'
import { useGetMonitorUrls } from '../../api/monitoring'
import { useCallMonitor } from '../call-staging/useCallMonitor'
import { useBargeIn } from '../call-staging/useBargeIn'
import { ListenButton } from '../call-staging/ListenButton'
import { ConferenceMonitor } from './ConferenceMonitor'
import { cn, elapsedSince } from '../../lib/utils'
import { statusConfig, dotColor } from '../../lib/callStatusConstants'

interface SimulationCallStatusPanelProps {
  callId: string
  listenUrl?: string
  controlUrl?: string
}

export function SimulationCallStatusPanel({
  callId,
  listenUrl: propListenUrl,
}: SimulationCallStatusPanelProps) {
  const { t } = useTranslation('simulations')
  const activeCall = useTelemetryStore((s) => s.activeCalls[callId])
  const sseConnectionState = useTelemetryStore((s) => s.sseConnectionState)
  const endCall = useEndCall()

  const [elapsed, setElapsed] = useState('')
  const [stuckWarning, setStuckWarning] = useState(false)
  const [showMonitor, setShowMonitor] = useState(false)

  const status = activeCall?.status
  const isCallActive = !!activeCall && status !== 'ended'

  // Fetch monitor URLs as fallback if SSE didn't provide them
  const monitorQuery = useGetMonitorUrls(isCallActive ? callId : undefined)
  const listenUrl = propListenUrl || activeCall?.listen_url || monitorQuery.data?.listen_url

  // Call monitoring (listen to live audio) — user opts in via the Listen button.
  const monitor = useCallMonitor({ listenUrl, enabled: false })

  // Barge-in (jump into call via browser mic)
  const bargeIn = useBargeIn()

  // Elapsed timer
  useEffect(() => {
    if (!activeCall || activeCall.status === 'ended') return
    const interval = setInterval(() => {
      setElapsed(elapsedSince(activeCall.started_at))
    }, 1000)
    // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
    // It has multiple writers (this effect plus user event handlers), so deriving
    // it during render is a state-machine redesign, not a mechanical fix — and the
    // UI has no JS test suite to catch a regression. Suppressed, not fixed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setElapsed(elapsedSince(activeCall.started_at))
    return () => clearInterval(interval)
  }, [activeCall])

  // Stuck-in-dialing detection: warn after 20s in 'initiated' with no progress
  useEffect(() => {
    // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
    // It has multiple writers (this effect plus user event handlers), so deriving
    // it during render is a state-machine redesign, not a mechanical fix — and the
    // UI has no JS test suite to catch a regression. Suppressed, not fixed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStuckWarning(false)
    if (!activeCall || activeCall.status !== 'initiated') return
    const timer = setTimeout(() => setStuckWarning(true), 20_000)
    return () => clearTimeout(timer)
  }, [activeCall?.call_sid, activeCall?.status])

  const handleEndCall = () => {
    if (!activeCall) return
    endCall.mutate(activeCall.call_sid)
  }

  const handleBargeIn = () => {
    if (!activeCall) return
    bargeIn.bargeIn(activeCall.call_sid)
  }

  const visual = status ? statusConfig[status] : undefined
  const statusLabel = status ? t(`calls:lifecycle.${status}`) : undefined

  // Pre-SSE state: we have a callId but no telemetry event yet
  const isWaitingForSSE = !activeCall

  return (
    <div className="rounded-lg border border-border bg-surface-card p-5">
      {/* Status indicator */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {isWaitingForSSE ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-info/20 px-3 py-1 text-sm font-medium text-info">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('callPanel.connectingVapi')}
            </span>
          ) : visual && status ? (
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium',
                visual.color,
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  dotColor[status],
                  visual.pulse && 'animate-pulse',
                )}
              />
              {statusLabel}
            </span>
          ) : null}

          {isCallActive && elapsed && (
            <span className="font-mono text-sm text-text-secondary">{elapsed}</span>
          )}
        </div>

        {activeCall && status !== 'ended' && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span>{t('callPanel.simulation')}</span>
            <span>·</span>
            <span>{activeCall.mode}</span>
          </div>
        )}
      </div>

      {/* Action buttons — End Call + Jump In + Listen */}
      {isCallActive && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleEndCall}
            disabled={endCall.isPending || status === 'cancelling'}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors',
              endCall.isPending || status === 'cancelling'
                ? 'cursor-not-allowed bg-surface-elevated text-text-muted'
                : 'bg-error text-white hover:bg-error/80',
            )}
          >
            {endCall.isPending || status === 'cancelling' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PhoneOff className="h-4 w-4" />
            )}
            {endCall.isPending || status === 'cancelling' ? t('callPanel.cancelling') : t('callPanel.endCall')}
          </button>

          {/* Listen — silent monitoring */}
          {!showMonitor && (
            <button
              onClick={() => setShowMonitor(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-success px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-success/80"
            >
              <Headphones className="h-4 w-4" />
              {t('callPanel.listen')}
            </button>
          )}

          {/* Jump In — only when call is active and not already barged in */}
          {!bargeIn.isConnected && !bargeIn.isConnecting && (
            <button
              onClick={handleBargeIn}
              className="inline-flex items-center gap-2 rounded-lg bg-info px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-info/80"
            >
              <PhoneForwarded className="h-4 w-4" />
              {t('callPanel.jumpIn')}
            </button>
          )}

          {/* Barge-in connecting state */}
          {bargeIn.isConnecting && (
            <button
              disabled
              className="inline-flex items-center gap-2 rounded-lg bg-surface-elevated px-5 py-2.5 text-sm font-medium text-text-muted cursor-not-allowed"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('callPanel.joining')}
            </button>
          )}
        </div>
      )}

      {/* Listen toggle + Barge-in controls */}
      {isCallActive && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ListenButton
            isListening={monitor.isListening}
            connectionState={monitor.connectionState}
            audioLevel={monitor.audioLevel}
            hasUrl={!!listenUrl}
            onToggle={monitor.toggleListening}
          />

          {/* Barge-in connected controls */}
          {bargeIn.isConnected && (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-info/15 px-3 py-1 text-xs font-medium text-info">
                <span className="h-2 w-2 rounded-full bg-info animate-pulse" />
                {t('callPanel.connectedYouAreIn')}
              </span>
              <button
                onClick={bargeIn.toggleMute}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                  bargeIn.isMuted
                    ? 'bg-warning/15 text-warning hover:bg-warning/25'
                    : 'bg-surface-elevated text-text-secondary hover:bg-surface-elevated/80',
                )}
              >
                {bargeIn.isMuted ? (
                  <MicOff className="h-3.5 w-3.5" />
                ) : (
                  <Mic className="h-3.5 w-3.5" />
                )}
                {bargeIn.isMuted ? t('callPanel.unmute') : t('callPanel.mute')}
              </button>
              <button
                onClick={bargeIn.hangUp}
                className="inline-flex items-center gap-1.5 rounded-lg bg-error/15 px-3 py-1.5 text-xs font-medium text-error transition-colors hover:bg-error/25"
              >
                <PhoneOff className="h-3.5 w-3.5" />
                {t('callPanel.leaveCall')}
              </button>
            </>
          )}
        </div>
      )}

      {/* Ended info */}
      {activeCall?.status === 'ended' && (
        <div className="mt-3 rounded-md bg-surface-elevated px-3 py-2 text-sm text-text-secondary">
          {t('callPanel.callEnded')}
          {activeCall.ended_by && (
            <>
              {' '}— {t('callPanel.endedBy', { by: '' })}
              <span className="font-medium text-text-primary">{activeCall.ended_by}</span>
            </>
          )}
          {activeCall.ended_reason && (
            <span className="text-text-muted"> ({activeCall.ended_reason})</span>
          )}
        </div>
      )}

      {/* Error messages */}
      {endCall.isError && (
        <p className="mt-2 text-sm text-error">
          {t('callPanel.errors.endCall', { message: (endCall.error as Error).message })}
        </p>
      )}
      {monitor.error && (
        <p className="mt-2 text-sm text-warning">{t('callPanel.errors.listen', { message: monitor.error })}</p>
      )}
      {bargeIn.error && (
        <p className="mt-2 text-sm text-error">{t('callPanel.errors.bargeIn', { message: bargeIn.error })}</p>
      )}

      {/* Stuck in dialing warning */}
      {stuckWarning && activeCall?.status === 'initiated' && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('callPanel.warnings.stuckDialing')}
        </div>
      )}

      {/* SSE connection warning */}
      {sseConnectionState !== 'connected' && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          <WifiOff className="h-4 w-4 shrink-0" />
          {t('callPanel.warnings.sseDown')}
        </div>
      )}

      {/* Conference Monitor Modal */}
      {showMonitor && isCallActive && (
        <ConferenceMonitor
          callId={callId}
          onClose={() => setShowMonitor(false)}
        />
      )}
    </div>
  )
}
